// src/pages/superadmin/SuperAdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, Server, Cpu, DollarSign, Users, Activity,
  RefreshCw, Clock, TrendingUp, TrendingDown, BarChart2,
  Globe, AlertTriangle, CheckCircle, XCircle, Shield,
  Plus, Package, Key, Download, Eye, Edit2, Trash2,
  ChevronLeft, ChevronRight, Search
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── SparkLine (inspiré de TakeoffLandingAnalytics) ──────────────────────────
const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 140 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#sg-${color.replace('#', '')})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// ─── StatCard (inspiré de TakeoffLandingAnalytics) ───────────────────────────
const StatCard = ({ title, value, sub, icon: Icon, color, trend, sparkData, onClick }) => (
  <div 
    onClick={onClick}
    className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300 cursor-pointer"
  >
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" style={{ color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      {sparkData && (
        <div className="mt-2 opacity-70">
          <SparkLine data={sparkData} color={color} height={36} width={130} />
        </div>
      )}
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-emerald-400" />}
          {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
            {sub}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── MiniBar (inspiré de TakeoffLandingAnalytics) ────────────────────────────
const MiniBar = ({ value, max, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(value / max) * 100}%`, background: color }}
      />
    </div>
  </div>
);

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    active: { label: 'Active', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    inactive: { label: 'Inactive', color: 'bg-red-500/20 text-red-400', icon: XCircle },
    warning: { label: 'Warning', color: 'bg-yellow-500/20 text-yellow-400', icon: AlertTriangle },
    healthy: { label: 'Healthy', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
    critical: { label: 'Critical', color: 'bg-red-500/20 text-red-400', icon: XCircle }
  };
  const c = config[status] || config.active;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

// ─── DonutChart (inspiré de TakeoffLandingAnalytics) ─────────────────────────
const DonutChart = ({ data = [], size = 120 }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = 42;
  const circ = 2 * Math.PI * r;
  let offset = circ * 0.25;
  
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="14" />
      {data.map((d, i) => {
        const angle = (d.value / total) * circ;
        const el = (
          <circle
            key={i}
            cx="50" cy="50" r={r} fill="none"
            stroke={d.color} strokeWidth="14"
            strokeDasharray={`${angle} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }}
          />
        );
        offset -= angle;
        return el;
      })}
      <text x="50" y="45" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">{total}</text>
      <text x="50" y="60" textAnchor="middle" fill="#94a3b8" fontSize="8">TOTAL</text>
    </svg>
  );
};

// ─── BarChartSVG (inspiré de TakeoffLandingAnalytics) ────────────────────────
const BarChartSVG = ({ data = [], colorA = '#38bdf8', colorB = '#f59e0b', labelKey = 'label', aKey = 'value', bKey = 'compare' }) => {
  const maxVal = Math.max(...data.flatMap(d => [d[aKey] || 0, d[bKey] || 0]), 1);
  const W = 500, H = 100, pad = 20, barW = Math.max(4, (W - pad * 2) / (data.length * 2.5));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 20}`} className="overflow-visible">
      {[0, 0.5, 1].map(t => (
        <line key={t} x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
          stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      ))}
      {data.map((d, i) => {
        const x = pad + i * ((W - pad * 2) / data.length);
        const hA = ((d[aKey] || 0) / maxVal) * H;
        const hB = ((d[bKey] || 0) / maxVal) * H;
        return (
          <g key={i}>
            <rect x={x} y={H - hA} width={barW} height={hA} fill={colorA} rx="2" opacity="0.85" />
            {bKey && d[bKey] !== undefined && (
              <rect x={x + barW + 2} y={H - hB} width={barW} height={hB} fill={colorB} rx="2" opacity="0.75" />
            )}
            <text x={x + barW} y={H + 14} textAnchor="middle" fill="#64748b" fontSize="7">
              {d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── AirportCard (comme TruckCard dans FuelView) ─────────────────────────────
const AirportCard = ({ airport, onEdit, onDelete, onViewAgents, onViewLicense }) => {
  const agentPercent = ((airport.used_agents || 0) / (airport.max_agents || 5)) * 100;
  const daysLeft = airport.license_days_left;
  const licenseColor = daysLeft === null ? '#64748b' : daysLeft < 30 ? '#f87171' : daysLeft < 90 ? '#fbbf24' : '#10b981';
  
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 hover:border-gray-600 transition-all group">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-white font-semibold">{airport.name}</h3>
          <p className="text-gray-400 text-xs">{airport.subdomain}.aerotrick.com</p>
        </div>
        <StatusBadge status={airport.is_active ? 'active' : 'inactive'} />
      </div>
      
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Agents</span>
            <span className="text-gray-300">{airport.used_agents || 0}/{airport.max_agents || 5}</span>
          </div>
          <MiniBar value={airport.used_agents || 0} max={airport.max_agents || 5} color="#38bdf8" />
        </div>
        
        {daysLeft !== undefined && (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">License</span>
              <span className="text-gray-300">{daysLeft === null ? 'Unlimited' : `${daysLeft} days left`}</span>
            </div>
            {daysLeft !== null && (
              <MiniBar value={daysLeft} max={365} color={licenseColor} />
            )}
          </div>
        )}
      </div>
      
      <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700">
        <button onClick={() => onViewAgents(airport)} className="flex-1 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs flex items-center justify-center gap-1">
          <Cpu className="w-3 h-3" /> Agents
        </button>
        <button onClick={() => onViewLicense(airport)} className="flex-1 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-xs text-purple-400 flex items-center justify-center gap-1">
          <Key className="w-3 h-3" /> License
        </button>
        <button onClick={() => onEdit(airport)} className="flex-1 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-xs text-blue-400">
          <Edit2 className="w-3 h-3 inline mr-1" /> Edit
        </button>
        <button onClick={() => onDelete(airport)} className="flex-1 py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-xs text-red-400">
          <Trash2 className="w-3 h-3 inline mr-1" /> Del
        </button>
      </div>
    </div>
  );
};

// ─── QuickActionCard ─────────────────────────────────────────────────────────
const QuickActionCard = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-2 p-3 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 transition-all group"
  >
    <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
      <Icon size={18} style={{ color }} />
    </div>
    <span className="text-xs text-gray-300">{label}</span>
  </button>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // États des données
  const [stats, setStats] = useState({
    totalAirports: 0,
    activeAirports: 0,
    totalStaff: 0,
    totalClients: 0,
    totalAgents: 0,
    activeAgents: 0,
    monthlyRevenue: 0
  });
  const [airports, setAirports] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueBySource, setRevenueBySource] = useState([]);
  const [trendData, setTrendData] = useState([]);
  
  // Sparkline mock data pour les tendances
  const revenueSparkline = [32000, 34500, 38000, 42000, 45000, 48000, 52000];
  const agentsSparkline = [12, 15, 18, 22, 24, 28, 32];
  const airportsSparkline = [8, 9, 10, 11, 12, 13, 14];

  const fetchStats = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/platform/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const fetchAirports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/airports/`, {
        params: { page: currentPage, limit: 12, search: searchTerm }
      });
      setAirports(response.data.airports || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching airports:', error);
    } finally {
      setLoading(false);
      setAnimated(true);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchStats();
    fetchAirports();
    const interval = setInterval(() => {
      fetchStats();
      fetchAirports();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchAirports]);

  // Filtrer les aéroports par statut
  const filteredAirports = airports.filter(airport => {
    if (filter === 'all') return true;
    if (filter === 'active') return airport.is_active === true;
    if (filter === 'inactive') return airport.is_active === false;
    if (filter === 'warning') return airport.license_days_left && airport.license_days_left < 30 && airport.license_days_left > 0;
    if (filter === 'critical') return airport.license_days_left === 0 || airport.license_days_left === null;
    return true;
  });

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setAnimated(false);
  };

  // Données pour le donut chart
  const donutData = [
    { label: 'Fuel', value: 42, color: '#f59e0b' },
    { label: 'Licenses', value: 28, color: '#8b5cf6' },
    { label: 'Services', value: 18, color: '#10b981' },
    { label: 'Other', value: 12, color: '#64748b' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-400" />
            Platform Admin
          </h1>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchAirports(); }}
          disabled={refreshing}
          className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Airports"
          value={stats.totalAirports}
          sub={`${stats.activeAirports} actifs`}
          icon={Building2}
          color="#f87171"
          trend="up"
          sparkData={airportsSparkline}
        />
        <StatCard
          title="Agents"
          value={`${stats.activeAgents}/${stats.totalAgents}`}
          sub="Actifs / Total"
          icon={Cpu}
          color="#38bdf8"
          sparkData={agentsSparkline}
        />
        <StatCard
          title="Staff"
          value={stats.totalStaff}
          sub="Tous aéroports"
          icon={Users}
          color="#4ade80"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${(stats.monthlyRevenue / 1000).toFixed(1)}k`}
          sub="Tous aéroports"
          icon={DollarSign}
          color="#fbbf24"
          trend="up"
          sparkData={revenueSparkline}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-3">
        <QuickActionCard icon={Plus} label="Add Airport" color="#3b82f6" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'airports' } }))} />
        <QuickActionCard icon={Package} label="Packages" color="#8b5cf6" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'packages' } }))} />
        <QuickActionCard icon={Key} label="Licenses" color="#f59e0b" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'licenses' } }))} />
        <QuickActionCard icon={Shield} label="Security" color="#ef4444" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'sysadmin' } }))} />
      </div>

      {/* Revenue Chart & Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Revenue Trends */}
        <div className="lg:col-span-2 bg-gray-800/60 rounded-xl border border-gray-700/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <BarChart2 size={15} className="text-emerald-400" />
              Revenue Trends (12 Months)
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block" />Revenue</span>
            </div>
          </div>
          <BarChartSVG data={trendData} colorA="#10b981" labelKey="label" aKey="value" />
        </div>

        {/* Revenue by Source */}
        <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Globe size={15} className="text-purple-400" />
            Revenue by Source
          </h3>
          <div className="flex flex-col items-center">
            <DonutChart data={donutData} size={130} />
            <div className="mt-3 space-y-1 w-full">
              {donutData.map(item => (
                <div key={item.label} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                    <span className="text-gray-300">{item.label}</span>
                  </div>
                  <span className="text-white font-semibold">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Airports Section Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-white font-semibold text-lg">Airports</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search airports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-48"
            />
          </div>
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {['all', 'active', 'inactive', 'warning', 'critical'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Airports Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAirports.map((airport, idx) => (
              <div key={airport.id} className={animated ? 'animate-fade-in' : ''} style={{ animationDelay: `${idx * 50}ms` }}>
                <AirportCard
                  airport={airport}
                  onViewAgents={(a) => console.log('View agents', a)}
                  onViewLicense={(a) => console.log('View license', a)}
                  onEdit={(a) => console.log('Edit', a)}
                  onDelete={(a) => console.log('Delete', a)}
                />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-gray-400 text-sm py-2">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default SuperAdminDashboard;